import {
  DELEGATION_MAX_DAYS,
  type Delegation,
  type Member,
  MONEY_LEVELS,
  type MoneyLevel,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useMemo, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const STATUS: Record<
  Delegation['status'],
  { label: string; tone: 'ink' | 'success' | 'warning' | 'danger' }
> = {
  active: { label: 'Действует', tone: 'success' },
  scheduled: { label: 'Запланировано', tone: 'ink' },
  ended: { label: 'Истекло', tone: 'warning' },
  revoked: { label: 'Отозвано', tone: 'danger' },
};

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function DelegationsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin', 'delegations'],
    queryFn: () => api<Delegation[]>('/admin/delegations'),
  });
  const members = useQuery({
    queryKey: ['admin', 'members'],
    queryFn: () => api<Member[]>('/admin/members'),
  });

  const staff = useMemo(
    () => (members.data ?? []).filter((m) => m.kind === 'staff' && m.status === 'active'),
    [members.data],
  );

  const [principalUserId, setPrincipal] = useState('');
  const [delegateUserId, setDelegate] = useState('');
  const [startsAt, setStartsAt] = useState(() => toLocalInput(new Date()));
  const [endsAt, setEndsAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return toLocalInput(d);
  });
  const [moneyLevel, setMoneyLevel] = useState<MoneyLevel>('none');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      api<Delegation>('/admin/delegations', {
        body: {
          principalUserId,
          delegateUserId,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          moneyLevel: me.tenant?.isOwner ? moneyLevel : 'none',
          note: note.trim() || null,
        },
      }),
    onSuccess: () => {
      toast('Замещение создано');
      setNote('');
      setErrors({});
      qc.invalidateQueries({ queryKey: ['admin', 'delegations'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) setErrors(err.fieldErrors());
      toast(err instanceof ApiError ? err.message : 'Не удалось создать замещение', 'danger');
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/admin/delegations/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      toast('Замещение отозвано');
      qc.invalidateQueries({ queryKey: ['admin', 'delegations'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Не удалось отозвать', 'danger'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!principalUserId) next.principalUserId = 'Выберите замещаемого';
    if (!delegateUserId) next.delegateUserId = 'Выберите заместителя';
    if (principalUserId && principalUserId === delegateUserId)
      next.delegateUserId = 'Заместитель должен быть другим человеком';
    const start = Date.parse(startsAt);
    const end = Date.parse(endsAt);
    if (!Number.isFinite(start) || !Number.isFinite(end)) next.endsAt = 'Укажите даты';
    else if (end <= start) next.endsAt = 'Окончание позже начала';
    else if (end - start > DELEGATION_MAX_DAYS * 86_400_000)
      next.endsAt = `Не больше ${DELEGATION_MAX_DAYS} дней`;
    setErrors(next);
    if (Object.keys(next).length) return;
    create.mutate();
  }

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Новое замещение"
        meta={`Заместитель получает права замещаемого на срок до ${DELEGATION_MAX_DAYS} дней. Деньги — только если владелец компании выдал уровень на этот срок.`}
      >
        <form className="grid max-w-xl gap-4" onSubmit={onSubmit}>
          <Field label="Кого замещают" error={errors.principalUserId}>
            <Select value={principalUserId} onChange={(e) => setPrincipal(e.target.value)} required>
              <option value="">Выберите сотрудника</option>
              {staff.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Кто замещает" error={errors.delegateUserId}>
            <Select value={delegateUserId} onChange={(e) => setDelegate(e.target.value)} required>
              <option value="">Выберите сотрудника</option>
              {staff.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Начало" error={errors.startsAt}>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </Field>
            <Field label="Окончание" error={errors.endsAt}>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </Field>
          </div>
          {me.tenant?.isOwner ? (
            <Field
              label="Деньги на срок замещения"
              hint="Без выдачи заместитель действует без денег замещаемого. Согласования с суммами (договоры, бюджет, смета, изменения, оплаты) переходят к заместителю, только если выдан уровень не ниже наибольшего уровня замещаемого; иначе их решают следующие по маршруту"
            >
              <Select value={moneyLevel} onChange={(e) => setMoneyLevel(e.target.value as MoneyLevel)}>
                {(Object.keys(MONEY_LEVELS) as MoneyLevel[]).map((k) => (
                  <option key={k} value={k}>
                    {MONEY_LEVELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label="Комментарий">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} />
          </Field>
          <div>
            <Button type="submit" disabled={create.isPending}>
              Создать замещение
            </Button>
          </div>
        </form>
      </Panel>

      <Panel title="Замещения" meta="Действующие, запланированные и недавние">
        {list.isLoading ? (
          <Spinner />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : !(list.data ?? []).length ? (
          <EmptyState title="Замещений пока нет">Создайте первое замещение формой выше.</EmptyState>
        ) : (
          <ul className="divide-y divide-line">
            {(list.data ?? []).map((d) => {
              const st = STATUS[d.status];
              return (
                <li
                  key={d.id}
                  className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-[15px] font-medium text-ink">
                      {d.delegateName} <span className="font-normal text-ink-2">за</span> {d.principalName}
                    </div>
                    <div className="mt-1 text-[13px] text-ink-2">
                      {dateTime(d.startsAt)} — {dateTime(d.endsAt)}
                      {d.moneyLevel !== 'none' ? (
                        <span> · деньги: {MONEY_LEVELS[d.moneyLevel]}</span>
                      ) : (
                        <span> · без денег замещаемого</span>
                      )}
                    </div>
                    {d.note ? <div className="mt-1 text-[13px] text-ink-3">{d.note}</div> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={st.tone}>{st.label}</Badge>
                    <HistoryButton table="delegations" rowId={d.id} title={`Замещение: ${d.delegateName}`} />
                    {d.status === 'active' || d.status === 'scheduled' ? (
                      <Button
                        variant="ghost"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (confirm('Отозвать это замещение?')) revoke.mutate(d.id);
                        }}
                      >
                        Отозвать
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
